import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { appleTokens as t, tint } from '../../../theme/appleTokens';

/**
 * Conteneur standard d'une page ERP : mêmes marges et même espacement vertical entre blocs partout.
 * `fill` : la page occupe toute la hauteur disponible (tableaux / plannings qui s'étirent).
 * `maxWidth` : largeur max du contenu (ex. 1200 pour les pages de formulaires), centré.
 */
export function PageContainer({ children, fill = false, maxWidth, sx, ...rest }) {
  return (
    <Box
      {...rest}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 2, md: 3 },
        pt: { xs: 2, md: 3 },
        pb: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        ...(fill ? { flex: 1, minHeight: 0 } : null),
        ...(maxWidth ? { maxWidth, mx: 'auto' } : null),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * En-tête standard : (retour) titre à gauche, actions à droite, onglets de vue dessous. Pas de sous-titre
 * descriptif (choix produit : gagner de la hauteur, le titre et la topbar suffisent) ; une info utile sous le
 * titre (statut, saison source…) passe par `meta`.
 */
export function PageHeader({ title, titleEnd, back, actions, tabs, meta, leading }) {
  return (
    <Box>
      {back ? (
        <ButtonBase
          component={RouterLink}
          to={back.to}
          sx={{
            ...t.type.small,
            fontWeight: 500,
            color: t.secondaryLabel,
            gap: 0.5,
            mb: 1,
            borderRadius: t.radius.sm,
            px: 0.5,
            ml: -0.5,
            '&:hover': { color: t.label },
          }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
          {back.label || 'Retour'}
        </ButtonBase>
      ) : null}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ pb: tabs ? 1.5 : 0 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {leading}
          <Box sx={{ minWidth: 0 }}>
            {/* `titleEnd` : contenu sur la ligne du titre (ex. pastilles de statut à côté du nom). */}
            <Stack direction="row" alignItems="center" columnGap={1.25} rowGap={0.75} flexWrap="wrap">
              <Typography component="h1" sx={{ ...t.type.pageTitle, color: t.label }}>
                {title}
              </Typography>
              {titleEnd}
            </Stack>
            {meta ? <Box sx={{ mt: 1 }}>{meta}</Box> : null}
          </Box>
        </Stack>
        {actions ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        ) : null}
      </Stack>
      {tabs}
    </Box>
  );
}

/**
 * Onglets de vue soulignés (Commandes / Articles…, Planning / Paiements…). `items` :
 * `[{ value, label, Icon?, badge?, badgeColor? }]`. `end` : contenu aligné à droite sur la ligne des onglets.
 */
export function ViewTabs({ items, value, onChange, end, sx }) {
  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      justifyContent="space-between"
      spacing={2}
      sx={{ borderBottom: '1px solid', borderColor: t.separator, ...sx }}
    >
      <Stack
        direction="row"
        spacing={2.5}
        // Défilement horizontal seulement (petits écrans), sans barre visible : le soulignement
        // de l'onglet actif reste DANS la rangée (bottom: 0) pour ne jamais créer de débordement vertical.
        sx={{ overflowX: 'auto', overflowY: 'hidden', minWidth: 0, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        {items.map(({ value: v, label, Icon, badge, badgeColor }) => {
          const selected = v === value;
          return (
            <ButtonBase
              key={v}
              onClick={() => onChange(v)}
              aria-current={selected ? 'page' : undefined}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.25,
                pt: 0.5,
                pb: 1.25,
                whiteSpace: 'nowrap',
                ...t.type.bodyStrong,
                fontWeight: selected ? 600 : 500,
                color: selected ? t.label : t.secondaryLabel,
                transition: 'color 0.15s ease',
                '&:hover': { color: t.label },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 2,
                  borderRadius: 2,
                  bgcolor: selected ? t.label : 'transparent',
                },
              }}
            >
              {Icon ? <Icon sx={{ fontSize: 18 }} /> : null}
              {label}
              {badge ? <CountBadge color={badgeColor}>{badge}</CountBadge> : null}
            </ButtonBase>
          );
        })}
      </Stack>
      {end ? <Box sx={{ pb: 1, flexShrink: 0 }}>{end}</Box> : null}
    </Stack>
  );
}

/** Petit compteur rond (onglets, navigation). */
export function CountBadge({ children, color }) {
  const bg = color || t.status.injured.color;
  return (
    <Box
      component="span"
      sx={{
        minWidth: 18,
        height: 18,
        px: 0.5,
        borderRadius: t.radius.pill,
        bgcolor: bg,
        color: '#FFFFFF',
        ...t.type.micro,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {children}
    </Box>
  );
}

/** Barre de filtres au-dessus d'une liste : filtres à gauche (retour à la ligne), `end` à droite. */
export function FilterBar({ children, end, sx }) {
  return (
    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={sx}>
      {children}
      {end ? <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>{end}</Box> : null}
    </Stack>
  );
}

/** Pastille d'icône carrée teintée (en-têtes de carte, KPI, dialogs). */
export function IconTile({ icon, color, size = 38 }) {
  if (!icon) return null;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: t.radius.md,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: color ? tint(color, 0.1) : t.surfaceHover,
        color: color || t.secondaryLabel,
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: Math.round(size * 0.5) } })}
    </Box>
  );
}
